import {
  PaginationState,
  SortingState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { UserConfig, rolesColumn, actionsColumn } from './table.config';
import { TableCustom } from '../../../UI/table/table';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectListItems
} from '../../../../redux/users/selectors';
import { users } from '../../../../redux/users/actions';
import { 
  getRealmUsers, 
  getRoleNames 
} from '../../../../service/user.service';
import MemberDialog, { type MemberPerson } from '../../members/MemberDialog';
import {
  confirmRemoveFromScope,
  confirmToggleActive,
  removeFromScope
} from '../../members/memberActions';
import { 
  Button,
  Modal,
  Popconfirm,
  Select, 
  message 
} from 'antd';
import { 
  issuedMessage, 
  targetLabel, 
  type InviteTarget 
} from '../../invitations/InviteModal';
import {
  listEntityInvitations,
  listOrganisationInvitations,
  resendOrganisationInvitation,
  revokeOrganisationInvitation,
  type InvitationRow
} from '../../../../service/invitation.service';
import { useUserAdministration } from '../../../hooks/useUserAdministration';
import { deactivateUser } from '../../../../service/userLifecycle.service';
import { selectCurrentWorkspace } from '../../../../redux/auth/selectors';
import UserType from './type';
import { buildInheritedRows } from './inheritedRows';
import {
  filterRows,
  hasActiveFilters,
  rowActionRules,
  workspaceAdministrable
} from '../../access/accessRules';
import {
  listEntityInheritedMembers,
  listInheritedMembers,
  type InheritedMember
} from '../../../../service/workspaceMembers.service';

/**
 * With `nodeId` + `level` set (the org/entity console) the table lists everyone under that
 * node from the paged members route; without them it lists the active workspace as before.
 * `readOnly` lists the accounts with no row click, edit, delete or lifecycle action.
 * In the workspace form `canManageMembers` (MEMBERSHIP manage) allows changing a role and
 * removing a member; suspending stays at organisation or entity level.
 */
function UserTable({ nodeId, level, readOnly = false, targets, organisationId, canManageInherited = false, canManageMembers = false }: { nodeId?: number; level?: 'organisation' | 'entity'; readOnly?: boolean; targets?: InviteTarget[]; organisationId?: number; canManageInherited?: boolean; canManageMembers?: boolean } = {}) {
  const dispatch = useDispatch();
  const [roleFilter, setRoleFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<'active' | 'suspended' | 'invited' | undefined>();
  const [whereFilter, setWhereFilter] = useState<number | undefined>();
  const filtering = !!(roleFilter || statusFilter || whereFilter);
  const workspaceForm = !(nodeId && level);
  const currentWorkspace = useSelector(selectCurrentWorkspace) as { id: number; name?: string } | undefined;
  const roleNodeId = nodeId ?? currentWorkspace?.id;
  const [roleOptions, setRoleOptions] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    if (!roleNodeId) return;
    let stale = false;
    getRoleNames(roleNodeId).then((list) => {
      if (stale) return;
      const names = Array.from(new Set(list.filter((r) => r.custom_role_id == null).map((r) => r.name)));
      setRoleOptions(names.map((name) => ({ value: name, label: name })));
    }).catch(() => {
      if (!stale) setRoleOptions([]);
    });
    return () => {
      stale = true;
    };
  }, [roleNodeId]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const { result: listResult, isLoading: listIsLoading } = useSelector(selectListItems);
  const [globalFilter, setGlobalFilter] = useState(listResult.search || '');
  const [editModalVisible, setEditModalVisible] = useState(false);
  // The row itself, not just its id: it is the only source carrying the user's role and
  // second-factor flag. `GET /api/v1/user/details` answers `roles: []` and no MFA field.
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [pagination, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25
  });
  /** A changed filter always restarts from page 1. */
  const changeFilter = <T,>(set: (value: T | undefined) => void, value: T | undefined) => {
    set(value ?? undefined);
    setPaginationState((p) => ({ ...p, pageIndex: 0 }));
  };

  // Deleting accounts stays Superadmin-only (PLATFORM_USER.MANAGE); an Admin resolves NONE
  // there, so the delete control is not rendered for them rather than left to answer 403 on
  // click. Deactivation is the lever an Admin has instead. There is no invite action here:
  // people join an organisation by invitation, from Settings -> Organisation invitations.
  const { administrabilityOf, callerRole, callerEmail } = useUserAdministration(nodeId);
  /** Workspace level: MEMBERSHIP manage, the member is below the viewer, not inherited. */
  const workspaceAllows = useCallback((row: UserType) => rowActionRules({
    level: 'workspace',
    canManageAccess: canManageMembers,
    administrable: workspaceAdministrable({
      inherited: Boolean(row.inherited),
      capabilityHeld: canManageMembers,
      callerRole,
      callerEmail,
      target: row as any
    })
  }), [canManageMembers, callerRole, callerEmail]);
  /** Whether the viewer may run row actions on this row at this level. */
  const allowsRemove = useCallback(
    (row: UserType) => workspaceForm ? workspaceAllows(row).remove : administrabilityOf(row as any).allowed,
    [workspaceForm, workspaceAllows, administrabilityOf]
  );
  const workspace = useSelector(selectCurrentWorkspace) as { id: number; name?: string } | undefined;
  const nodeLabel = nodeId && level
    ? ((targets ?? []).filter((target) => target.id === nodeId).map((target) => target.name)[0] ?? 'this ' + level)
    : workspace?.name ?? 'this workspace';
  const scopeNodeIds = nodeId && level
    ? (targets && targets.length > 0 ? targets.map((target) => target.id) : [nodeId])
    : workspace ? [workspace.id] : [];
  /** `id -> authenticator enrolled`, read live from Keycloak. See `loadFactors`. */
  const [totpById, setTotpById] = useState<Record<string, boolean>>({});

  /**
   * The workspace list reports the second factor from a mirror table that lags, so the
   * authenticator column reads Keycloak instead and is merged in here by account id. This
   * is a separate request on purpose: it is not paginated and does not change while the
   * operator types in the search box, so it is refreshed after a change rather than on
   * every keystroke.
   */
  const loadFactors = useCallback(async () => {
    // The node list already carries each account's live factors.
    if (nodeId) return;
    const realmUsers = await getRealmUsers();
    setTotpById(
      realmUsers.reduce<Record<string, boolean>>((byId, account) => {
        if (account.id) byId[account.id] = account.totp === true;
        return byId;
      }, {})
    );
  }, [nodeId]);

  useEffect(() => {
    loadFactors();
  }, [loadFactors]);

  const fetchData = useCallback(() => {
    dispatch<any>(users.list({
      options: {
        page: pagination.pageIndex + 1,
        items: pagination.pageSize,
        sorting,
        globalFilter,
        search: globalFilter,
        nodeId,
        level,
        filters: nodeId && level
          ? {
            role: roleFilter,
            status: statusFilter === 'invited' ? undefined : statusFilter,
            nodeId: whereFilter
          }
          : undefined
      }
    }));
  }, [dispatch, pagination, sorting, globalFilter, nodeId, level, roleFilter, statusFilter, whereFilter]);

  // People who reach an entity through a role granted above it: listed as extra rows.
  const [inheritedMembers, setInheritedMembers] = useState<InheritedMember[]>([]);
  const [inheritedTick, setInheritedTick] = useState(0);
  // Entity console: the entity's inheritors. Workspace form: the workspace's.
  const inheritedEntity = level === 'entity' && nodeId ? nodeId : undefined;
  const inheritedWorkspace = workspaceForm ? currentWorkspace?.id : undefined;
  const inheritedKey = inheritedEntity ? 'e' + inheritedEntity : inheritedWorkspace ? 'w' + inheritedWorkspace : undefined;
  // Clear only when the entity changes; a refresh tick keeps the rows until the new list lands.
  useEffect(() => {
    setInheritedMembers([]);
  }, [inheritedKey]);
  useEffect(() => {
    if (!inheritedKey) return;
    let stale = false;
    (inheritedEntity ? listEntityInheritedMembers(inheritedEntity) : listInheritedMembers(inheritedWorkspace as number))
      .then((list) => !stale && setInheritedMembers(list))
      .catch(() => !stale && setInheritedMembers([]));
    return () => {
      stale = true;
    };
  }, [inheritedKey, inheritedEntity, inheritedWorkspace, inheritedTick]);

  const inheritedRows: UserType[] = useMemo(
    () => buildInheritedRows(
      inheritedMembers,
      (listResult.items ?? []).map((row: UserType) => row.id),
      globalFilter,
      canManageInherited
    ),
    [inheritedMembers, listResult.items, globalFilter, canManageInherited]
  );

  // Pending invitations whose grants touch this console's nodes: listed as extra rows.
  const [invitations, setInvitations] = useState<InvitationRow[]>([]);
  const loadInvitations = useCallback(() => {
    // An entity console reads the entity's own list: an entity-only admin cannot reach the organisation's.
    if (!nodeId || !level || (level !== 'entity' && !organisationId)) {
      setInvitations([]);
      return Promise.resolve();
    }
    return (level === 'entity' ? listEntityInvitations(nodeId) : listOrganisationInvitations(organisationId as number))
      .then(setInvitations)
      .catch(() => setInvitations([]));
  }, [organisationId, nodeId, level]);
  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  const scopeKey = scopeNodeIds.join(',');
  const invitationRows: UserType[] = useMemo(() => {
    const scope = new Set(scopeKey ? scopeKey.split(',').map(Number) : []);
    const kindOf = (id: number) => (targets ?? []).find((t) => t.id === id)?.kind ?? level ?? 'entity';
    const needle = globalFilter.trim().toLowerCase();
    return invitations
      .filter((inv) => inv.status === 'PENDING' && inv.grants.some((g) => scope.has(g.node_id)))
      .filter((inv) => !needle || [inv.email, inv.name]
        .some((part) => (part ?? '').toLowerCase().includes(needle)))
      .filter((inv) => !roleFilter || inv.grants.some((g) => (g.role ?? g.custom_role_name) === roleFilter))
      .filter((inv) => !whereFilter || inv.grants.some((g) => g.node_id === whereFilter))
      .map((inv) => ({
        id: 'inv-' + inv.id,
        email: inv.email,
        firstName: inv.name ?? '',
        lastName: '',
        enabled: true,
        roles: inv.grants.map((g) => g.role ?? g.custom_role_name ?? 'custom'),
        memberships: inv.grants.map((g) => ({
          node_id: g.node_id,
          node_kind: kindOf(g.node_id),
          node_name: g.node_name,
          role: g.role ?? g.custom_role_name
        })),
        invitation: {
          id: inv.id,
          expiresAt: inv.expires_at,
          expired: new Date(inv.expires_at).getTime() < Date.now()
        }
      } as unknown as UserType));
  }, [invitations, scopeKey, targets, level, globalFilter, roleFilter, whereFilter]);
  // Active/Suspended hide invitations; Invited shows only them.
  const shownInvitationRows = useMemo(
    () => statusFilter === 'active' || statusFilter === 'suspended' ? [] : invitationRows,
    [statusFilter, invitationRows]
  );

  const rows: UserType[] = useMemo(
    () => {
      const mapped = (listResult.items ?? []).map((row: UserType) => ({
        ...row,
        // `undefined` while the Keycloak read is in flight: the column draws that as
        // "unknown", never as "no authenticator".
        totpEnabled: nodeId ? row.totpEnabled : totpById[String(row.id)]
      }));
      // The workspace list is not filtered by the server, so role and status apply here.
      const serverRows = statusFilter === 'invited'
        ? []
        : workspaceForm ? filterRows(mapped as any[], {
          role: roleFilter,
          status: statusFilter
        }) as UserType[] : mapped;
      if (pagination.pageIndex !== 0) return serverRows;
      // Invitations lead page 1; inherited people follow the server rows, and any
      // active filter hides them (they are not filtered server-side).
      const withInherited = !filtering ? serverRows.concat(inheritedRows) : serverRows;
      return (shownInvitationRows as UserType[]).concat(withInherited);
    },
    [listResult.items, totpById, nodeId, workspaceForm, roleFilter, inheritedRows, pagination.pageIndex, filtering, statusFilter, shownInvitationRows]
  );

  const resendInvitation = async (row: UserType) => {
    if (!organisationId || !row.invitation) return;
    try {
      const outcome = issuedMessage(await resendOrganisationInvitation(organisationId, row.invitation.id));
      message[outcome.type](outcome.text);
      await loadInvitations();
    } catch (error: any) {
      message.error(error?.response?.data?.detail ?? 'Could not resend the invitation.');
    }
  };

  const revokeInvitation = (row: UserType) => {
    if (!organisationId || !row.invitation) return;
    const id = row.invitation.id;
    Modal.confirm({
      title: `Revoke the invitation to ${row.email}?`,
      content: 'The link stops working. You can invite them again later.',
      okText: 'Revoke',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await revokeOrganisationInvitation(organisationId, id);
          message.success('Invitation revoked.');
          await loadInvitations();
        } catch (error: any) {
          message.error(error?.response?.data?.detail ?? 'Could not revoke the invitation.');
        }
      }
    });
  };

  // The roles cell leads with the role held at the level being viewed.
  const baseColumns = useMemo(
    () => UserConfig.columns.map((column: any) => column.accessorKey === 'roles' ? rolesColumn(level) : column),
    [level]
  );

  const openRow = useCallback((row: any) => {
    setEditModalVisible(true);
    setSelectedUser(row);
  }, []);

  /** The row as the drawer wants it; the workspace list carries no memberships, so one is made. */
  const personOf = (row: any): MemberPerson => ({
    ...row,
    memberships: row.memberships?.length || !workspace ? row.memberships ?? [] : [{
      node_id: workspace.id,
      node_kind: 'workspace',
      node_name: workspace.name ?? 'this workspace',
      role: typeof row.roles?.[0] === 'string' ? row.roles[0] : null
    }]
  });

  const afterChange = useCallback(() => {
    setInheritedTick((tick) => tick + 1);
    fetchData();
    loadFactors();
  }, [fetchData, loadFactors]);

  const columns = useMemo(
    () => readOnly
      ? [...baseColumns]
      : [
        ...baseColumns, actionsColumn({
          administrabilityOf: workspaceForm
            ? (row: UserType) => workspaceAllows(row).manage ? {
              allowed: true
            } as any : {
              allowed: false,
              reason: 'Your role cannot change this member.'
            } as any
            : administrabilityOf,
          allowSuspend: !workspaceForm,
          nodeLabel,
          canManageInherited,
          onManage: openRow,
          onResendInvitation: resendInvitation,
          onRevokeInvitation: revokeInvitation,
          onToggleActive: (row: UserType) => confirmToggleActive(personOf(row), afterChange),
          onRemove: (row: UserType) => row.inherited
            ? confirmRemoveFromScope(
              personOf(row),
              row.inherited.sourceNodeId != null ? [row.inherited.sourceNodeId] : [],
              row.inherited.nodeName ?? nodeLabel,
              afterChange
            )
            : confirmRemoveFromScope(personOf(row), scopeNodeIds, nodeLabel, afterChange)
        })
      ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseColumns, administrabilityOf, workspaceForm, workspaceAllows, nodeLabel, openRow, afterChange, readOnly, canManageInherited, workspace, nodeId, targets, organisationId, loadInvitations]
  );

  const table = useReactTable({
    data: rows,
    columns,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    enableRowSelection: (row: any) => !row.original.inherited && !row.original.invitation,
    manualSorting: true,
    getRowId: (row: any) => row.id,
    state: {
      sorting,
      pagination
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    // Invited shows only the invitation rows, so there is exactly one page.
    pageCount: statusFilter === 'invited' ? 1 : listResult.pagination.totalPages,
    onPaginationChange: setPaginationState
  });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Keep "Online" current while the page is open and visible.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') fetchData();
    }, 60000);
    return () => window.clearInterval(timer);
  }, [fetchData]);

  // Keep the open drawer's person current after a change (role, status), so its role
  // Select and Undo work from the reloaded data, not the row captured when it opened.
  useEffect(() => {
    setSelectedUser((current: any) => {
      if (!current) return current;
      const fresh = rows.find((row: any) => row.id === current.id);
      return fresh ?? current;
    });
  }, [rows]);

  const selectedRows: any[] = table.getSelectedRowModel().flatRows.map((row: any) => row.original);
  /** Run one action per selected row the caller may administer; the server still decides each. */
  const bulk = async (action: (row: any) => Promise<unknown>, verb: string) => {
    const eligible = selectedRows.filter((row) => allowsRemove(row));
    let done = 0;
    for (const row of eligible) {
      try {
        await action(row);
        done += 1;
      } catch {
        // counted below
      }
    }
    const skipped = selectedRows.length - done;
    if (done > 0) message.success(`${done} ${verb}.`);
    if (skipped > 0) message.warning(`${skipped} could not be changed.`);
    table.resetRowSelection();
    afterChange();
  };

  return (
    <div>
      {(
        <div className="flex flex-wrap gap-2 mb-3">
          <Select
            allowClear
            size="middle"
            style={{ width: 160 }}
            placeholder="Role"
            value={roleFilter}
            options={roleOptions}
            onChange={(value) => changeFilter(setRoleFilter, value)}
          />
          <Select
            allowClear
            size="middle"
            style={{ width: 160 }}
            placeholder="Status"
            value={statusFilter}
            options={[
              { 
                value: 'active', 
                label: 'Active' 
              }, { 
                value: 'suspended', 
                label: 'Suspended'
              }, ...(organisationId ? [{
                value: 'invited',
                label: 'Invited'
              }] : [])
            ]}
            onChange={(value) => changeFilter(setStatusFilter, value)}
          />
          {targets && targets.length > 0 && (
            <Select
              allowClear
              size="middle"
              style={{ width: 160 }}
              placeholder="Where"
              value={whereFilter}
              options={targets.map((target) => ({ 
                value: target.id, 
                label: targetLabel(target) 
              }))}
              onChange={(value) => changeFilter(setWhereFilter, value)}
            />
          )}
          <Button
            disabled={!hasActiveFilters({
              role: roleFilter,
              status: statusFilter,
              nodeId: whereFilter,
              search: globalFilter
            })}
            onClick={() => {
              setRoleFilter(undefined);
              setStatusFilter(undefined);
              setWhereFilter(undefined);
              setGlobalFilter('');
              setPaginationState((p) => ({ ...p, pageIndex: 0 }));
            }}
          >Reset filters</Button>
        </div>
      )}
      {!readOnly && selectedRows.length > 0 && (
        <div className="flex items-center gap-2 mb-3">
          <span>{selectedRows.length} selected</span>
          {!workspaceForm && <Popconfirm
            title={`Suspend ${selectedRows.length} account${selectedRows.length === 1 ? '' : 's'}?`}
            okText="Suspend"
            okButtonProps={{ danger: true }}
            onConfirm={() => bulk(async (row) => { 
              await deactivateUser(row.email); 
            }, 'suspended')}
          >
            <Button>Suspend</Button>
          </Popconfirm>}
          <Popconfirm
            title={`Remove ${selectedRows.length} ${selectedRows.length === 1 ? 'person' : 'people'} from ${nodeLabel}?`}
            okText="Remove"
            okButtonProps={{ danger: true }}
            onConfirm={() => bulk((row) => removeFromScope(personOf(row), scopeNodeIds), 'removed')}
          >
            <Button danger>{`Remove from ${nodeLabel}`}</Button>
          </Popconfirm>
        </div>
      )}
      <TableCustom
        hideSelectionBar
        showDetailsLink={false}
        onRowClick={readOnly ? undefined : (rowId: string, row: any) => {
          if ((row.inherited && !canManageInherited) || row.invitation) return;
          openRow(row);
        }}
        id='user'
        table={table}
        showDrawer={false}
        isLoading={listIsLoading}
        totalRecords={(statusFilter === 'invited' ? 0 : Number(listResult.pagination.totalRecords) || 0)
          + (filtering ? 0 : inheritedRows.length) + shownInvitationRows.length}
        quickSearch={{
          onSearchChange: (value: string) => setGlobalFilter(value),
          onClear: () => setGlobalFilter(''),
          value: globalFilter
        }}
      />
      {
        editModalVisible && selectedUser && <MemberDialog
          open
          targets={targets}
          adminNodeId={selectedUser.inherited?.sourceNodeId ?? nodeId}
          onClose={() => {
            setEditModalVisible(false);
            setSelectedUser(null);
          }}
          onChanged={afterChange}
          person={personOf(selectedUser)}
          scopeNodeIds={selectedUser.inherited?.sourceNodeId != null
            ? scopeNodeIds.concat(selectedUser.inherited.sourceNodeId)
            : scopeNodeIds}
          scopeLabel={nodeLabel}
          tabs={workspaceForm ? ['access'] : ['access', 'security', 'danger']}
        />
      }
    </div >
  );
}
export default UserTable;
